import React, {useState, useEffect} from "react";
import InputComponent from "../../../shared/ui/common/InputComponent";
import {isEmpty} from "underscore";

const LoggedInUserState = require("shared/ui/user/LoggedInUserState"),
    Configuration = require("shared/services/Configuration"),
    Storage = require("shared/services/Storage"),
    SystemMessages = require("editor/services/SystemMessages");

export default function ShopifyStoreSettings() {
    const config = {
            username: "",
            password: "",
            storeUrl: "",
        },
        styleClasses = {
            mainContainer: "row shopify-setting",
            inputField: "shopify-setting-input",
            submitBtn: "shopify-setting-button",
            inputError: "validation-error",
            icon: "icon",
            notVisibleIcon: "not-visible",
            visibleIcon: "visible"
        },
        [settings, setSettings] = useState(config),
        [passwordVisible, setPasswordVisible] = useState(false),
        NOTIFICATION_MESSAGE = {
            INVALID_FORM: "Please fill out all the details.",
            PUT_REQUEST_FAILURE: "Failure occured while updating the details.",
            PUT_REQUEST_SUCCESS: "Successfully updated all the details.",
        },
        delay = 2000;

    const creatorSettingApi = () => {
        const companyId = LoggedInUserState.attributes.company.id,
            endpointUrl = `${Configuration.creatorSettingApiBaseUrl}/v1/settings/${companyId}`,
            token = Storage.Local.get(Storage.Keys.LOGIN, "token") || Storage.Session.get(Storage.Keys.LOGIN, "token"),
            bearerToken = `Bearer ${token}`;

        return {
            endpointUrl,
            bearerToken,
        };
    };

    const getShopifySettings = () => {
        const {endpointUrl, bearerToken} = creatorSettingApi(),
            options = {
                method: "GET",
                headers: {
                    Authorization: bearerToken,
                },
            },
            settingName = "connect";

        fetch(`${endpointUrl}?name=${settingName}`, options)
            .then((res) => res.json())
            .then((data) => {
                const connect = data.connect;
                if (connect) {
                    const userSettings = connect.ipd.details[0];
                    setSettings({
                        username: userSettings.username,
                        password: userSettings.password,
                        storeUrl: userSettings.url,
                    });
                    Storage.Local.update(Storage.Keys.CREATOR_CONNECT, connect);
                }
            })
            .catch((err) => console.log(err));
    };

    const updateShopifySetting = () => {
        let data = {
            settings: {
                onloadActionEnabled: true,
                connect: {
                    ipd: {
                        enabled: true,
                        details: [
                            {
                                password: settings.password,
                                type: "shopify",
                                url: settings.storeUrl,
                                username: settings.username,
                            },
                        ],
                    },
                    enabled: true,
                },
            },
        };

        const {endpointUrl, bearerToken} = creatorSettingApi(),
            options = {
                method: "PUT",
                headers: {
                    Authorization: bearerToken,
                },
                contentType: "application/json; charset=utf-8",
                body: JSON.stringify(data),
            };

        fetch(endpointUrl, options)
            .then((res) => res.json())
            .then((data) => {
                if (data.Attributes) {
                    SystemMessages.notify(NOTIFICATION_MESSAGE.PUT_REQUEST_SUCCESS, delay);
                } else {
                    SystemMessages.notify(NOTIFICATION_MESSAGE.PUT_REQUEST_FAILURE, delay);
                }
            })
            .catch((err) => {
                SystemMessages.notify(NOTIFICATION_MESSAGE.PUT_REQUEST_FAILURE, delay);
                console.log(err);
            });
    };

    useEffect(() => {
        const connect = Storage.Local.get(Storage.Keys.CREATOR_CONNECT);
        if (isEmpty(connect)) {
            getShopifySettings();
        } else if(connect.ipd) {
            const userSettings = connect.ipd.details[0];
            setSettings({
                username: userSettings.username,
                password: userSettings.password,
                storeUrl: userSettings.url,
            });
        }
    }, []);

    const validateStoreUrl = (url) => {
        let isValidUrl: boolean;

        try {
            const transformedUrl = new URL(url);
            isValidUrl = transformedUrl.protocol === "https:" ? true : false;
        } catch (err) {
            isValidUrl = false;
            return isValidUrl;
        }

        return isValidUrl;
    };

    const toggleClass = (isValid, targetElement) => {
        const {inputField, inputError} = styleClasses;

        if (isValid) {
            targetElement.className = inputField;
        } else {
            targetElement.className = `${inputField} ${inputError}`;
        }
    };

    const handleInputChange = (e) => {
        const {name, value} = e.target;

        if (name === "storeUrl") {
            const isValidUrl = validateStoreUrl(value);
            toggleClass(isValidUrl, e.target);
        } else {
            const isValid = !isEmpty(value);
            toggleClass(isValid, e.target);
        }

        setSettings({
            ...settings,
            [name]: value,
        });
    };

    const isFormValid = () => {
        const {username, password, storeUrl} = settings;
        return (!isEmpty(username) && !isEmpty(password) && !isEmpty(storeUrl) && validateStoreUrl(storeUrl));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (isFormValid()) {
            updateShopifySetting();
        } else {
            SystemMessages.notify(NOTIFICATION_MESSAGE.INVALID_FORM, delay);
        }
    };

    return (
        <section className={styleClasses.mainContainer}>
            <section className="col-xs-6">
                <h4>Shopify</h4>
                <p className="intro">
                    With Shopify Connect module, you can compose intelligent Creator widgets built from the product and
                    pricing details in your Shopify backend.
                </p>
                <p>
                    To learn more about adding Shopify-powered Behaviors to your Creator experiences, contact <a href="mailto:<%- config.salesEmail %>">sales.zmags.com</a>
                </p>
            </section>

            <section className="col-xs-6">
                <InputComponent
                    classNames={styleClasses.inputField}
                    name="username"
                    label="Username"
                    type="text"
                    value={settings.username}
                    onChange={handleInputChange}
                    key="username"
                />

                <InputComponent
                    classNames={styleClasses.inputField}
                    name="password"
                    label="Password"
                    type={passwordVisible ? "text" : "password"}
                    value={settings.password}
                    onChange={handleInputChange}
                    key="password"
                />

                {settings.password.length > 0 && (
                    <button
                        className={`${styleClasses.icon} ${passwordVisible ? styleClasses.visibleIcon : styleClasses.notVisibleIcon}`}
                        onClick={() => setPasswordVisible(!passwordVisible)}
                    ></button>
                )}

                <InputComponent
                    classNames={styleClasses.inputField}
                    name="storeUrl"
                    label="Store URL"
                    type="text"
                    placeholder="https://"
                    value={settings.storeUrl}
                    onChange={handleInputChange}
                    key="storeUrl"
                />

                <div className={styleClasses.submitBtn}>
                    <button onClick={handleSubmit}>Submit</button>
                </div>
            </section>
        </section>
    );
}



// WEBPACK FOOTER //
// ./src/editor/ui/settings/ShopifyStoreSettings.tsx